import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, PageHeaderWithBack, useToast } from "@/components/common";
import { Pagination } from "@/components/common/Pagination";
import { useAuth } from "@/context/AuthContext";
import { UploadTemplatesModal } from "./components/UploadTemplatesModal";
import { TemplateSection } from "./components/TemplateSection";
import { JobChecklistSection } from "./components/JobChecklistSection";
import { AddChecklistItemModal } from "./components/AddChecklistItemModal";
import { EditChecklistItemModal } from "./components/EditChecklistItemModal";
import { TemplateCategory, } from "@/types/template.types";
import { templatesService } from "@/api/services/templates.service";
import { checklistTemplatesService } from "@/api/services/checklist-templates.service";
import { formatDateOnlyDisplay } from "@/utils/dateTime";
import styles from "./Templates.module.css";
const CHECKLIST_PAGE_SIZE = 10;
// Helper to format date for display
const formatDate = (dateString) => {
    if (!dateString)
        return "-";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime()))
            return dateString;
        return formatDateOnlyDisplay(dateString);
    }
    catch (e) {
        return dateString;
    }
};
export const Templates = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showToast } = useToast();
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showAddChecklistModal, setShowAddChecklistModal] = useState(false);
    const [editingChecklistItem, setEditingChecklistItem] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [checklistItems, setChecklistItems] = useState([]);
    const [checklistPage, setChecklistPage] = useState(1);
    const [checklistPageSize] = useState(CHECKLIST_PAGE_SIZE);
    const [checklistTotalPages, setChecklistTotalPages] = useState(1);
    const [checklistLoading, setChecklistLoading] = useState(true);
    const fetchTemplates = async () => {
        try {
            setIsLoading(true);
            const response = await templatesService.getTemplates();
            const rawTemplates = Array.isArray(response.data) ? response.data : [];
            const formattedTemplates = rawTemplates.map((t) => ({
                ...t,
                lastModified: formatDate(t.lastModified),
                lastModifiedValue: t.lastModified
                    ? new Date(t.lastModified).getTime()
                    : 0,
            }));
            setTemplates(formattedTemplates);
        }
        catch (err) {
            showToast("Failed to load templates", "error");
        }
        finally {
            setIsLoading(false);
        }
    };
    const fetchChecklistTemplates = useCallback(async () => {
        try {
            setChecklistLoading(true);
            const response = await checklistTemplatesService.getChecklistTemplates(checklistPage, checklistPageSize);
            const { data, pagination } = response.data;
            setChecklistItems(data.map((t) => ({ id: t.id, text: t.description })));
            setChecklistTotalPages(pagination.totalPages);
        }
        catch (err) {
            showToast("Failed to load checklist templates", "error");
        }
        finally {
            setChecklistLoading(false);
        }
    }, [checklistPage, checklistPageSize]);
    useEffect(() => {
        fetchChecklistTemplates();
    }, [fetchChecklistTemplates]);
    useEffect(() => {
        fetchTemplates();
    }, []);
    const jobTemplates = templates.filter((t) => t.category === TemplateCategory.JOB);
    const siteTemplates = templates.filter((t) => t.category === TemplateCategory.SITE_ESTIMATE);
    const businessTemplates = templates.filter((t) => t.category === TemplateCategory.BUSINESS);
    const handleOpenModal = () => {
        setShowUploadModal(true);
    };
    const handleCloseModal = () => {
        setShowUploadModal(false);
    };
    const handleUploadTemplates = async (uploadedFiles, category, subtitle) => {
        try {
            showToast("Uploading templates...", "info");
            for (const uploadedFile of uploadedFiles) {
                if (!uploadedFile.file)
                    continue;
                const formData = new FormData();
                formData.append("file", uploadedFile.file);
                formData.append("category", category);
                formData.append("name", uploadedFile.name);
                if (subtitle) {
                    formData.append("subtitle", subtitle);
                }
                await templatesService.uploadTemplates(formData);
            }
            showToast("Templates uploaded successfully", "success");
            fetchTemplates();
            handleCloseModal();
        }
        catch (err) {
            console.error("Failed to upload templates:", err);
            showToast("Failed to upload templates", "error");
        }
    };
    const handleDownload = async (templateId) => {
        try {
            const response = await templatesService.getDownloadUrl(templateId);
            const url = response.data.url;
            const link = document.createElement("a");
            link.href = url;
            const template = templates.find((t) => t.id === templateId);
            link.setAttribute("download", template?.name || "template");
            link.setAttribute("target", "_blank"); // Open in new tab if direct download isn't forced
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
        catch (err) {
            console.error("Failed to download template:", err);
            showToast("Failed to download template", "error");
        }
    };
    const handleDelete = async (templateId) => {
        try {
            await templatesService.deleteTemplate(templateId);
            showToast("Template deleted successfully", "success");
            fetchTemplates();
        }
        catch (err) {
            console.error("Failed to delete template:", err);
            showToast("Failed to delete template", "error");
        }
    };
    const handleOpenAddChecklistModal = useCallback(() => {
        setShowAddChecklistModal(true);
    }, []);
    const handleCloseAddChecklistModal = useCallback(() => {
        setShowAddChecklistModal(false);
    }, []);
    const handleAddChecklistItem = useCallback(async (text) => {
        try {
            await checklistTemplatesService.createChecklistTemplate({
                description: text,
            });
            showToast("Checklist item added", "success");
            await fetchChecklistTemplates();
        }
        catch (err) {
            showToast("Failed to add checklist item", "error");
        }
    }, [fetchChecklistTemplates, showToast]);
    const handleRemoveChecklistItem = useCallback(async (id) => {
        try {
            await checklistTemplatesService.deleteChecklistTemplate(id);
            showToast("Checklist item removed", "success");
            await fetchChecklistTemplates();
        }
        catch (err) {
            showToast("Failed to remove checklist item", "error");
        }
    }, [fetchChecklistTemplates, showToast]);
    const handleOpenEditChecklistModal = useCallback((item) => {
        setEditingChecklistItem(item);
    }, []);
    const handleCloseEditChecklistModal = useCallback(() => {
        setEditingChecklistItem(null);
    }, []);
    const handleEditChecklistItem = useCallback(async (id, description) => {
        try {
            await checklistTemplatesService.updateChecklistTemplate(id, {
                description,
            });
            showToast("Checklist item updated", "success");
            handleCloseEditChecklistModal();
            await fetchChecklistTemplates();
        }
        catch (err) {
            showToast("Failed to update checklist item", "error");
        }
    }, [fetchChecklistTemplates, showToast, handleCloseEditChecklistModal]);
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Templates", onBack: () => navigate(-1) }), _jsxs("div", { className: styles.templatesHeader, children: [_jsx("h2", { className: styles.formsSectionTitle, children: "TEMPLATES" }), _jsxs("button", { className: styles.newFormBtn, onClick: handleOpenModal, children: [_jsx("span", { className: styles.plusIcon, children: "+" }), " Upload Template"] })] }), isLoading ? (_jsx("div", { style: { padding: "40px", textAlign: "center" }, children: "Loading templates..." })) : (_jsxs(_Fragment, { children: [_jsx(TemplateSection, { title: "JOB TEMPLATES", templates: jobTemplates, onDownload: handleDownload, onDelete: handleDelete }), _jsx(TemplateSection, { title: "SITE / ESTIMATE TEMPLATES", templates: siteTemplates, onDownload: handleDownload, onDelete: handleDelete }), _jsx(TemplateSection, { title: "BUSINESS TEMPLATES", templates: businessTemplates, onDownload: handleDownload, onDelete: handleDelete })] })), _jsx(JobChecklistSection, { items: checklistItems, loading: checklistLoading, onAddItem: handleOpenAddChecklistModal, onRemoveItem: handleRemoveChecklistItem, onEditClick: handleOpenEditChecklistModal }), checklistTotalPages > 1 && (_jsx(Pagination, { currentPage: checklistPage, totalPages: checklistTotalPages, pageSize: checklistPageSize, onPageChange: setChecklistPage })), _jsx(UploadTemplatesModal, { isOpen: showUploadModal, onClose: handleCloseModal, onSave: handleUploadTemplates }), _jsx(AddChecklistItemModal, { isOpen: showAddChecklistModal, onClose: handleCloseAddChecklistModal, onAdd: handleAddChecklistItem }), _jsx(EditChecklistItemModal, { isOpen: !!editingChecklistItem, item: editingChecklistItem, onClose: handleCloseEditChecklistModal, onSave: handleEditChecklistItem })] }));
};
